
import { Component, Vue } from "vue-property-decorator";
import { haversineDistance } from "@/utils/misc";
import QrCodeScannerWrapper from "@/components/qrcode/QrCodeScannerWrapper.vue";
import { mdiClose, mdiQrcode } from "@mdi/js";
import SvgIcon from "@jamescoyle/vue-icon";

interface Mounting {
  id: number;
  token: string;
  Building?: {
    Address?: any;
    Boxes?: any[];
  };
  projectRef?: string;
  technicianCompany?: string;
  technicianName?: string;
  imei?: string;
}

interface Box {
  externalId: string | null;
  activationCode: string | null;
  locationToken?: string | null;
}

@Component({
  components: { QrCodeScannerWrapper, SvgIcon },
})
export default class MountingPage extends Vue {
  private token: string | null = null;
  private mounting: Mounting | null = null;
  private loadingQrCodeStreamer = false;
  private loadingSubmit = false;
  private userLatitude: number | null = null;
  private userLongitude: number | null = null;
  private distance: number | null = null;
  private MAX_DISTANCE = 100;
  private scanQrcodeMasterlock = false;
  private scanQrcodeBridge = false;
  private scanAddQrCodeBox = {};
  private link: string | null = null;
  private boxes: Box[] = [];
  private newBox: Box = { externalId: null, activationCode: null };
  private mdiQrcode = mdiQrcode;
  private submitDialog = false;

  get boxesAlreadyAdded() {
    return this.mounting?.Building?.Boxes || [];
  }

  get validationMounting(): string | null {
    const { mounting, boxes, boxesAlreadyAdded, distance, MAX_DISTANCE } = this;

    if (!mounting?.technicianCompany || !mounting?.technicianName || !mounting?.imei || !mounting?.projectRef) {
      return this.$t("Mounting.missingFields") as string;
    }

    if ((boxes?.length || 0) + (boxesAlreadyAdded?.length || 0) === 0) {
      return this.$t("Mounting.noBoxes") as string;
    }

    if (!distance) { return this.$t("Mounting.noDistance") as string; }

    if (distance > MAX_DISTANCE) { return this.$t("Mounting.tooFarFromBuilding") as string; }

    return null; // tout est OK
  }

  get isMountingValid(): boolean {
    return this.validationMounting === null;
  }

  public mounted() {
    if (this.$route.params.token) {
      this.token = this.$route.params.token as string;
      this.getMountingByToken(this.token).then(() => {
        if (!this.mounting?.Building?.Address?.latitude) {
          this.$store.commit("alerts/displayError", {
            msg: this.$i18n?.t("Mounting.missingBuildingLatitude"),
          });
        } else if (!this.mounting?.Building?.Address?.longitude) {
          this.$store.commit("alerts/displayError", {
            msg: this.$i18n?.t("Mounting.missingBuildingLongitude"),
          });
        } else {
          this.getUserPosition().then(() => {
            if (this.userLatitude && this.userLongitude) {
              this.distance = haversineDistance(
                this.mounting!.Building!.Address!.latitude,
                this.mounting!.Building!.Address!.longitude,
                this.userLatitude,
                this.userLongitude,
              );
            }
          });
        }
      });
    }
  }

  public getMountingByToken(token: string) {
    this.boxes = [];
    return this.$store
      .dispatch("public/mountings/readByToken", { token })
      .then((data: Mounting) => {
        this.mounting = data;
      });
  }

  public getUserPosition() {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.userLatitude = position.coords.latitude;
            this.userLongitude = position.coords.longitude;
            resolve({ userLatitude: this.userLatitude, userLongitude: this.userLongitude });
          },
          (error) => {
            // tslint:disable-next-line:no-console
            console.error("Geolocation error:", error);
            this.$store.commit("alerts/displayError", {
              msg: this.$i18n?.t("Mounting.geolocError"),
            });
            reject(error);
          },
        );
      } else {
        // tslint:disable-next-line:no-console
        console.warn("Geolocation not supported.");
        this.$store.commit("alerts/displayError", {
          msg: this.$i18n?.t("Mounting.geolocNotSupportedError"),
        });
        reject();
      }
    });
  }

  public save(cause: string) {
    this.loadingSubmit = true;
    return this.$store
      .dispatch("public/mountings/update", {
        token: this.token,
        mounting: this.mounting,
        boxes: this.boxes,
      })
      .then((data: any) => {
        this.getMountingByToken(this.token);
        if (cause === "save") {
          this.$store.commit("alerts/displaySuccess", {
            msg: this.$i18n?.t("Mounting.alert.mountingUpdated"),
          });
        } else if (cause === "submit") {
          if (this.isMountingValid) {
            this.$store.commit("alerts/displaySuccess", {
              msg: this.$i18n?.t("Mounting.alert.mountingSheetValidated"),
            });
            this.link = data.link;
            this.submitDialog = true;
          } else {
            this.$store.commit("alerts/displayError", {
              msg: this.validationMounting,
            });
          }
        }
      })
      .catch((error: any) => {
        this.$store.commit("alerts/displayHttpError", { error });
      })
      .finally(() => {
        this.loadingSubmit = false;
      });
  }

  public onDecodeBridge(decodedString: string) {
    if (decodedString) {
      this.mounting.imei = decodedString;
      this.scanQrcodeBridge = false;
    }
  }

  public onDecode(decodedString: string) {
    if (decodedString) {
      this.loadingQrCodeStreamer = true;
      this.$nextTick().then(() => {
        const [externalId, activationCode] = decodedString.split(":");
        if (!externalId || !activationCode) {
          this.$store.commit("alerts/displayError", {
            msg: this.$i18n?.t("connectedBoxOnboarding.unknownQrCode"),
          });
        } else {
          this.boxes.push({ externalId, activationCode });
        }
        this.loadingQrCodeStreamer = false;
      });
    }
  }

  public onDecodeAddQrCodeBox(decodedString: string, box: any, index: number) {
    if (decodedString) {
      this.scanAddQrCodeBox[index] = false;
      if (this.boxes.some((b) => b.locationToken === decodedString)) {
        this.$store.commit("alerts/displayError", {
          msg: this.$i18n?.t("Mounting.duplicateQrCode"),
        });
      } else {
        box.locationToken = decodedString;
        this.$store.commit("alerts/displaySuccess", {
          msg: this.$i18n?.t("Mounting.qrCodeAdded"),
        });
      }
    }
  }

  public addMasterLock() {
    if (this.newBox.externalId && this.newBox.activationCode) {
      this.boxes.push(this.newBox);
      this.newBox = { externalId: null, activationCode: null };
    }
  }

  public removeMasterLock(i: number) {
    this.boxes.splice(i, 1);
  }
}
